// Five random words per player before the match: only 5 of 5 passes, failed words are retried,
// passed words stay passed.
import type { VoiceProfileInfo, VoiceTestInfo } from "@shared/guess/api";
import { AnimatePresence, motion } from "motion/react";
import { useCallback, useEffect, useRef, useState } from "react";
import { Avatar } from "../../components/Avatar";
import { Btn } from "../../components/Button";
import { Segmented, Toggle } from "../../components/Controls";
import { Check, RotateCcw, X } from "../../components/Icon";
import { toast } from "../../components/Toast";
import { fmt, useI18n } from "../../i18n";
import { formatDate } from "../../lib/format";
import { haptic, play } from "../../lib/sfx";
import type { Listener, Utterance } from "./audio/listener";
import type { GuessBackend, GuessConfig } from "./backend";
import { useGuessDict } from "./i18n";
import { MicMeter } from "./ui";

export interface VoicePass {
  lang: "ka" | "en";
  calibration: { noiseDb: number; speechDb: number; avgMs: number } | null;
  viaProfile: boolean;
  /** the passed test (online rooms hand it to the server as proof) */
  testId?: string;
}

const PROFILE_DAYS = 30;

export function VoiceCheck({
  player,
  listener,
  backend,
  config,
  isOwner,
  profile,
  onPassed,
}: {
  player: { name: string; avatar: number };
  listener: Listener;
  backend: GuessBackend;
  config: GuessConfig;
  isOwner: boolean;
  profile: VoiceProfileInfo | null;
  onPassed(p: VoicePass): void;
}) {
  const t = useGuessDict();
  const { lang: uiLang } = useI18n();
  const profileOk = isOwner && profile?.status === "passed" && Date.now() - profile.testedAt < PROFILE_DAYS * 86_400_000;
  const [useProfile, setUseProfile] = useState<boolean>(!!profileOk);
  const [lang, setLang] = useState<"ka" | "en">(profileOk ? profile!.lang : uiLang === "ka" ? "ka" : "en");
  const [keep, setKeep] = useState<boolean>(!!profile?.keepSamples);
  const [test, setTest] = useState<VoiceTestInfo | null>(null);
  const [current, setCurrent] = useState(0);
  const [busy, setBusy] = useState(false);
  const [last, setLast] = useState<{ ok: boolean; heard: string; n: number } | null>(null);
  const [level, setLevel] = useState(-140);
  const [hearing, setHearing] = useState(false);
  const [passed, setPassed] = useState(false);
  const busyRef = useRef(false);
  const testRef = useRef<VoiceTestInfo | null>(null);
  const currentRef = useRef(0);
  testRef.current = test;
  currentRef.current = current;

  // a fresh set of five words whenever the language (or the keep option) changes
  useEffect(() => {
    if (useProfile) return;
    let alive = true;
    setTest(null);
    setPassed(false);
    setLast(null);
    backend
      .voiceTest(lang, isOwner && keep)
      .then((tst) => {
        if (!alive) return;
        setTest(tst);
        setCurrent(0);
      })
      .catch(() => toast(t.errors.server, "bad"));
    return () => {
      alive = false;
    };
  }, [lang, keep, useProfile, backend, isOwner, t]);

  useEffect(() => {
    listener.setLang?.(lang === "ka" ? "ka-GE" : "en-US");
  }, [listener, lang]);

  const attempt = useCallback(
    async (u: Utterance) => {
      const tst = testRef.current;
      if (!tst || busyRef.current || tst.passed) return;
      busyRef.current = true;
      setBusy(true);
      const word = currentRef.current;
      try {
        const r = await backend.voiceAttempt(tst.id, word, u, listener.calibration());
        setTest(r.test);
        setLast({ ok: r.wordOk, heard: r.heard, n: Date.now() });
        if (r.wordOk) {
          play("ding", { volume: 0.7 });
          haptic(14);
        } else {
          play("buzz", { volume: 0.5 });
        }
        if (r.test.passed) {
          setPassed(true);
          play("streak");
          window.setTimeout(() => onPassed({ lang, calibration: listener.calibration(), viaProfile: false, testId: r.test.id }), 1100);
        } else {
          // next word that still needs a pass, after the current one (wrapping around)
          const words = r.test.words;
          for (let k = 1; k <= words.length; k++) {
            const i = (word + k) % words.length;
            if (!words[i].ok) {
              setCurrent(i);
              break;
            }
          }
        }
      } catch {
        toast(t.errors.network, "bad");
      } finally {
        busyRef.current = false;
        setBusy(false);
      }
    },
    [backend, listener, onPassed, lang, t],
  );

  useEffect(() => {
    if (useProfile) {
      listener.setActive(false);
      return;
    }
    listener.setActive(!passed);
    const offs = [
      listener.on("level", (db) => setLevel(db)),
      listener.on("start", () => setHearing(true)),
      listener.on("drop", () => setHearing(false)),
      listener.on("end", (u) => {
        setHearing(false);
        void attempt(u);
      }),
    ];
    return () => {
      offs.forEach((o) => o());
      listener.setActive(false);
    };
  }, [listener, attempt, passed, useProfile]);

  const target = test?.words[current];
  const micState = busy ? "checking" : hearing ? "hearing" : passed ? "off" : "listening";

  if (useProfile && profile) {
    return (
      <div className="mx-auto max-w-xl text-center">
        <Avatar index={player.avatar} size={96} mood="happy" disc={false} className="mx-auto" />
        <h2 className="display mt-4 text-3xl">{player.name}</h2>
        <p className="mt-3 inline-flex items-center gap-2 rounded-full bg-ground-3 px-4 py-2 font-bold text-good">
          <Check size={18} /> {fmt(t.testProfile, { date: formatDate(profile.testedAt, uiLang) })}
        </p>
        <div className="mt-8 grid gap-3 sm:grid-cols-2">
          <Btn variant="soft" icon={<RotateCcw size={18} />} onClick={() => setUseProfile(false)}>
            {t.testRetake}
          </Btn>
          <Btn game="guess" onClick={() => onPassed({ lang: profile.lang, calibration: profile.calibration, viaProfile: true })}>
            {t.testUseProfile}
          </Btn>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-2xl">
      <div className="flex items-center gap-4">
        <Avatar index={player.avatar} size={64} mood={passed ? "happy" : hearing ? "talk" : "idle"} disc={false} />
        <p className="text-lg text-ink-2">{fmt(t.testSub, { name: player.name })}</p>
      </div>

      <div className="mt-5 flex flex-wrap items-center gap-3">
        <span className="label !mb-0">{t.testLang}</span>
        <Segmented<"ka" | "en">
          value={lang}
          onChange={(l) => setLang(l)}
          options={[
            { value: "ka", label: t.langs.ka },
            { value: "en", label: t.langs.en },
          ]}
        />
      </div>

      {/* the five words */}
      <ol className="mt-6 grid grid-cols-5 gap-2" aria-label={t.testTitle}>
        {(test?.words ?? Array.from({ length: 5 }, () => null)).map((w, i) => {
          const isCur = !!w && i === current && !passed;
          return (
            <li
              key={i}
              className="grid h-14 place-items-center rounded-2xl text-xs font-extrabold sm:h-16 sm:text-sm"
              style={{
                background: w?.ok ? "var(--color-good)" : isCur ? "var(--color-guess)" : "var(--color-ground-3)",
                color: w?.ok ? "#06240f" : isCur ? "#2a0f00" : "var(--color-ink-2)",
                boxShadow: isCur ? "0 5px 0 var(--color-guess-deep)" : undefined,
              }}
            >
              {w ? (w.ok ? <Check size={22} /> : w.attempts > 0 ? <span className="flex items-center gap-1"><X size={14} />{w.attempts}</span> : i + 1) : "·"}
            </li>
          );
        })}
      </ol>

      <div className="mt-8 grid min-h-[150px] place-items-center rounded-[28px] bg-ground-2 px-4 py-6 text-center">
        <AnimatePresence mode="wait">
          {passed ? (
            <motion.p key="passed" initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="display text-4xl text-good">
              {t.testPassed}
            </motion.p>
          ) : target ? (
            <motion.div key={`${test?.id}-${current}`} initial={{ y: 10, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: -10, opacity: 0 }}>
              <div className="font-bold text-ink-2">{test!.words[current].attempts ? t.testRetry : t.testSay}</div>
              <div className="display mt-1 text-[clamp(40px,11vw,72px)] leading-none" data-voice-word>
                {target.word}
              </div>
            </motion.div>
          ) : (
            <span className="text-ink-3">…</span>
          )}
        </AnimatePresence>
      </div>

      <div className="mt-3 h-7 text-center font-bold" aria-live="polite">
        {last && !passed && (
          <span className={last.ok ? "text-good" : "text-bad"}>{last.heard ? fmt(t.testHeard, { text: last.heard }) : t.testNothing}</span>
        )}
      </div>

      <div className="mt-2">
        <MicMeter level={level} state={micState} label={passed ? "" : busy ? t.testChecking : t.testListening} />
      </div>
      {level > -140 && level < -58 && !hearing && !busy && !passed && <p className="mt-2 text-center text-sm text-ink-3">{t.testTooQuiet}</p>}

      {isOwner && config.samplesAvailable && (
        <div className="mt-6">
          <Toggle checked={keep} onChange={setKeep} label={fmt(t.testKeep, { days: config.sampleDays })} />
        </div>
      )}
    </div>
  );
}
